import IdleTimer from 'exacta-material-react/lib/src/components/IdleTimer';
import { TargettedSystems } from 'nots-window-renderer';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';

import DateTime from '../../../components/DateTime';
import { ComposedActions } from '../../../helpers/actions';
import { DisplayConstants, IdleTimeout, S80Monitors } from '../../../helpers/constants';
import { DemoInteractionBlocker } from '../../common-components/DemoInteractionBlocker';
import GuidedContainer from '../../common-components/GuidedContainer';
import { MultimediaInteractionBlocker } from '../../common-components/MultimediaBlocker';
import { PauseOverlay } from '../../common-components/PauseOverlay';
import StartLesson from '../../common-components/StartLesson';
import WindowDisplayManager from '../../common-components/WindowDisplayManager';
import MultimediaDialog from '../../MultimediaDialog';
import MonitorContext from '../../S80/MonitorContext';
import { HermesysColors } from '../Colors';
import { HermMainSizes } from '../Dimentions';
import { TopMenuBar } from './TopMenuBar';

export interface IHermsysMainProps {}

const alarmAnimation = keyframes`
 0% { background: red; color: white; }
 100% { background: transparent; color: red; }
`;

const Container = styled.div`
  width: ${HermMainSizes.main.width}px;
  height: ${HermMainSizes.main.height}px;
  background: ${HermesysColors.main.background};
  color: ${HermesysColors.White};
  font-family: Lucida Sans Typewriter;
`;

const TitleBar = styled.div`
  padding: 10px;
`;

const TabBar = styled.div`
  display: flex;
  padding-left: 10px;
`;

const TabBarItems = styled.div<any>`
  padding: 15px;
  padding-top: 5px;
  padding-bottom: 5px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: solid 1px ${HermesysColors.main.dimWhite};
  margin-right: 10px;
  margin-top: 10px;
  cursor: pointer;
  border-radius: 3px;
  ${({ active }) => {
    if (active) {
      return `background: rgba(81,90,113,1)`;
    }
  }}
`;

const ContentContainer = styled.div`
  height: 84%;
  margin-right: 20px;
  border: solid 1px ${HermesysColors.main.dimWhite};
  margin-top: 8px;
  margin-left: 10px;
`;

const StatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  padding-left: 10px;
  padding-right: 15px;
  padding-top: 2px;
`;

const AlarmPane = styled.div`
  color: red;
  display: inline-block;
  margin-left: 10px;
  margin-top: 2px;
  animation-name: ${alarmAnimation};
  animation-duration: 1.5s;
  animation-iteration-count: infinite;
`;

const RadioLetters = styled.div`
  background: ${HermesysColors.main.greenText};
  color: ${HermesysColors.White};
  display: inline-block;
  margin: 0px 2px;
  padding: 0px 3px;
  border-radius: 3px;
`;

const ActivePlanDiv = styled.div`
  color: yellow;
`;

const tabItems = [
  { name: 'Plans View', id: 'Tab_plans_view' },
  { name: 'Supervision View', id: 'Tab_sup_view' },
  { name: 'Tactical Plan', id: 'Tab_tac_plan' },
  { name: 'Profile Plan', id: 'Tab_profile_plan' },
  { name: 'Conf Plan', id: 'Tab_conf_plan' },
  { name: 'Grp Call Plan', id: 'Tab_Grp_call' },
  { name: 'Remote Control', id: 'Tab_Remote_control' },
  { name: 'Readiness', id: 'Tab_rediness' },
];

const onFormatDate = (moment: any) => moment.format('DD-MM-YYYY').toUpperCase();

const onFormatUTCTime = (moment: any) =>
  `${moment.utc().format('HH:mm:ss')} UTC`;

export function HermsysMain(props: IHermsysMainProps) {
  const dispatch = useDispatch();
  const { tab_buttons, ...otherData } = useSelector(
    (state: any) => state.executor.hermesys
  );

  const onTabClick = (id) => {
    dispatch(
      ComposedActions.PressGeneralButtonSwitch.aCreator({
        id,
        isActive: true,
      })
    );
  };

  return (
    <Container>
      <MonitorContext.Provider
        value={{
          monitor: S80Monitors.UPPER,
        }}
      >
        <TitleBar>Navantia Communication Control System - </TitleBar>
        <StartLesson preferredDialogMonitor="upper" />
        <PauseOverlay />
        <DemoInteractionBlocker />
        <MultimediaInteractionBlocker />
        <IdleTimer timeout={IdleTimeout} logoutOnTimeout={false} />
        <GuidedContainer data-area="guided-container" />
        <div id="window_backdrop" />
        <TopMenuBar />
        <TabBar>
          {tabItems.map((tabItem) => (
            <TabBarItems
              key={tabItem.name}
              id={tabItem.id}
              active={tab_buttons[tabItem.id].active}
              onClick={() => onTabClick(tabItem.id)}
            >
              <div>{tabItem.name}</div>
              <ActivePlanDiv>{otherData[`${tabItem.id}_active`]}</ActivePlanDiv>
            </TabBarItems>
          ))}
        </TabBar>
        <ContentContainer data-name="ContentContainer">
          <MultimediaDialog />
          <WindowDisplayManager
            area={DisplayConstants.ScreenView}
            targetSystem={TargettedSystems.HERMESYS}
            monitor={DisplayConstants.UpperMonitor}
            generalData={otherData}
          />
          <WindowDisplayManager
            area={DisplayConstants.DialogView}
            targetSystem={TargettedSystems.HERMESYS}
            monitor={DisplayConstants.UpperMonitor}
            showBackDrop={true}
          />
          <WindowDisplayManager
            area={DisplayConstants.GeneralDisplayArea}
            targetSystem={TargettedSystems.HERMESYS}
            monitor={DisplayConstants.UpperMonitor}
            showBackDrop={true}
          />
        </ContentContainer>
        <StatusBar>
          <div>
            Operator: comadmin |<AlarmPane>Alarms: 56</AlarmPane>
          </div>
          <div>
            Current Radio Silence: <RadioLetters>H</RadioLetters>
            <RadioLetters>V</RadioLetters>
            <RadioLetters>U</RadioLetters>
          </div>
          <div>
            <DateTime isLive onFormat={onFormatDate} />{' '}
            <DateTime isLive onFormat={onFormatUTCTime} />
          </div>
        </StatusBar>
      </MonitorContext.Provider>
    </Container>
  );
}
